import React, { useCallback, useMemo } from 'react';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import type { CommonNavMenuButtonProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/types.tsx';
import { CreateHubModalTrigger } from '@jira/servicedesk__customer-service-hubs/src/CreateHubModalTrigger.tsx';
import { csmExperienceDetailsRoute } from '@jira/router-routes__help-center-settings-routes/src/csmExperienceDetailsRoute.tsx';
import { helpCenterSettingsDetailsRoute } from '@jira/router-routes__help-center-settings-routes/src/helpCenterSettingsDetailsRoute.tsx';
import AddIcon from '@atlaskit/icon/core/add';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { generatePath, useRouterActions } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

const createExperienceButtonIcon: CommonNavMenuButtonProps['icon'] = (iconProps) => (
	<AddIcon {...iconProps} size="small" />
);

export const CreateExperienceButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();

	const onClick = useCallback(
		(_e: React.MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'customerExperiencesAddCustomerExperience', {
				entry: 'Nav4Sidebar',
			});
		},
		[],
	);

	const handleCreateHub = useCallback(
		(data?: { customerHubId: string; projectKey: string }) => {
			if (data) {
				const path = generatePath(
					fg('csm_appification_standalone_navigation')
						? csmExperienceDetailsRoute.path
						: helpCenterSettingsDetailsRoute.path,
					{
						helpCenterId: data.customerHubId,
						projectKey: data.projectKey,
					},
				);
				push(path);
			}
		},
		[push],
	);

	const entryPointProps = useMemo(() => ({ onCreateHub: handleCreateHub }), [handleCreateHub]);

	return (
		<CreateHubModalTrigger entryPointProps={entryPointProps}>
			{({ buttonRef }) => (
				<Tooltip content={formatMessage(createExperienceMessage)}>
					<CommonNavMenuButton
						ref={buttonRef}
						icon={createExperienceButtonIcon}
						label={formatMessage(createExperienceMessage)}
						onClick={onClick}
					/>
				</Tooltip>
			)}
		</CreateHubModalTrigger>
	);
};

const createExperienceMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.ui.create-experience',
	defaultMessage: 'Create experience',
	description:
		'Used as the tooltip text for an icon button that when clicked opens the create customer experience modal',
});
